import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { readWorkspace, writeWorkspace } from '../lib/workspaceStorage';
import { storageDegraded } from '../lib/safeStorage';
import { mergeProfileDraft, profileError } from '../lib/profileDraft';
import { BEFORE_REFRESH } from './UpdateNotice';
import '../styles/profileExperience.css';
const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api`;
const AGE = 24 * 60 * 60 * 1000;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const copy = value => JSON.parse(JSON.stringify(value));
const validShape = (value, fresh) => value && typeof value === 'object' && !Array.isArray(value) && Object.keys(fresh).every(key => {
  const v = value[key], f = fresh[key];
  return Array.isArray(f) ? Array.isArray(v) && v.length < 1000 && v.every(x => typeof x === 'string' && x.length < 2048)
    : typeof v === typeof f && (typeof v !== 'string' || v.length < 100000);
});
export function restoreProfileDraft(saved, fresh, owner, kind, now = Date.now()) {
  if (!saved || saved.owner !== owner || saved.kind !== kind || !Number.isFinite(saved.time)
      || saved.time > now || now - saved.time > AGE || !validShape(saved.form, fresh) || !validShape(saved.baseline, fresh)) return null;
  const onlyFormFields = value => Object.fromEntries(Object.keys(fresh).map(key => [key, value[key]]));
  const pending = saved.pending && typeof saved.pending.creating === 'boolean' && validShape(saved.pending.expected, fresh)
    ? { creating: saved.pending.creating, expected: onlyFormFields(saved.pending.expected),
        submitted: validShape(saved.pending.submitted, fresh) ? onlyFormFields(saved.pending.submitted) : onlyFormFields(saved.pending.expected) } : null;
  return { form: mergeProfileDraft(onlyFormFields(saved.baseline), onlyFormFields(saved.form), fresh), pending };
}

export function useProfileEditor({ kind, values, payload, validate }) {
  const { user } = useAuth();
  const owner = user?.id || user?._id || '';
  const key = `profile-draft:${kind}:${owner}`;
  const readPath = kind === 'venue' ? '/venues/me' : '/organizers/me';
  const createPath = kind === 'venue' ? '/venues' : '/organizers';
  const [record, setRecord] = useState(null), [form, setFormState] = useState(() => values(null));
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(''), [error, setError] = useState('');
  const [notice, setNotice] = useState(''), [needsCheck, setNeedsCheck] = useState(false);
  const [storageWarning, setStorageWarning] = useState(false);
  const live = useRef(null), locked = useRef(false), baseline = useRef(values(null));
  const current = useRef(form), currentRecord = useRef(null), pending = useRef(null), initial = useRef(true);
  const options = useRef({ values, payload, validate }); options.current = { values, payload, validate };
  const dirty = !same(form, baseline.current);
  const persist = (draft = current.current) => {
    if (!owner || live.current?.key !== key) return;
    const data = same(draft, baseline.current) && !pending.current ? null : {
      owner, kind, time: Date.now(), baseline: baseline.current, form: draft, pending: pending.current,
    };
    writeWorkspace(key, data);
    setStorageWarning(storageDegraded() || !same(readWorkspace(key), data));
  };
  const setForm = next => {
    if (locked.current || live.current?.key !== key) return;
    const value = typeof next === 'function' ? next(current.current) : next;
    current.current = value; setFormState(value); setNotice(''); setError(''); persist(value);
  };
  const install = (data, next) => {
    currentRecord.current = data; baseline.current = options.current.values(data);
    current.current = next; setRecord(data); setFormState(next); persist(next);
  };
  const matchesPending = fresh => pending.current?.expected && Object.keys(fresh).every(k => same(fresh[k], pending.current.expected[k]));
  const read = async () => {
    try { return (await axios.get(API + readPath, { timeout: 15000 })).data || null; }
    catch (e) { if (e.response?.status === 404) return null; throw e; }
  };
  const reload = async (preserve = true) => {
    const scope = live.current;
    if (locked.current || !owner || scope?.key !== key) return null;
    locked.current = true; setBusy(true); setLoadError(''); setError('');
    try {
      const data = await read();
      if (live.current !== scope) return null;
      if (data && (typeof data !== 'object' || !data.id)) throw new Error('invalid_profile');
      const fresh = options.current.values(data);
      const saved = preserve ? null : restoreProfileDraft(readWorkspace(key), fresh, owner, kind);
      if (saved?.pending) pending.current = saved.pending;
      if (data && matchesPending(fresh)) {
        // A user may have kept editing after a lost response. Confirm the old
        // save without replacing those newer edits, including after a reload.
        const local = preserve ? current.current : saved?.form || fresh;
        const next = mergeProfileDraft(pending.current.submitted || pending.current.expected, local, fresh);
        pending.current = null; setNeedsCheck(false); install(data, next);
        setNotice(same(next, fresh) ? 'Kaydı kontrol ettik: değişikliklerin kaydedilmiş. Yeniden göndermene gerek yok.'
          : 'Önceki kaydın doğrulandı. Sonradan yazdıkların taslak olarak korundu; hazır olduğunda kaydedebilirsin.');
      } else {
        const hadPending = !!pending.current;
        const next = preserve ? mergeProfileDraft(baseline.current, current.current, fresh) : saved?.form || fresh;
        const unresolvedCreate = hadPending && pending.current.creating && !data;
        if (!unresolvedCreate) pending.current = null;
        setNeedsCheck(unresolvedCreate); install(data, next);
        setNotice(unresolvedCreate ? 'Yeni profil kaydının sonucu henüz doğrulanamadı. Biraz sonra kaydı tekrar kontrol et; bilgilerin korunuyor.'
          : hadPending ? 'Güncel kayıt kontrol edildi. Yazdıkların korundu; karşılaştırıp yeniden kaydedebilirsin.'
            : !same(next, fresh) ? 'Kaydedilmemiş taslağın geri yüklendi. Kontrol edip kaydet.'
              : preserve ? 'Güncel profil bilgileri yüklendi.' : '');
      }
      initial.current = false;
      return data;
    } catch (e) {
      if (live.current === scope) {
        const message = 'Profil bilgileri alınamadı. Yazdıkların korunuyor; bağlantını kontrol edip tekrar dene.';
        if (initial.current) setLoadError(message); else setError(message);
      }
      return null;
    } finally {
      if (live.current === scope) { locked.current = false; setBusy(false); setLoading(false); }
    }
  };
  useEffect(() => {
    const scope = { key }; live.current = scope; locked.current = false; pending.current = null; initial.current = true;
    baseline.current = options.current.values(null); current.current = baseline.current; currentRecord.current = null;
    setRecord(null); setFormState(current.current); setLoading(true); setNeedsCheck(false); setNotice(''); setError('');
    if (owner) reload(false); else { setLoadError('Oturum doğrulanamadı. Lütfen yeniden giriş yap.'); setLoading(false); }
    return () => { if (live.current === scope) live.current = null; };
    // An identity-specific scope also rejects first-effect responses after StrictMode replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    const unload = event => { if (locked.current || !same(current.current, baseline.current) && storageDegraded()) { event.preventDefault(); event.returnValue = ''; } };
    const update = event => { if (locked.current || needsCheck || storageWarning && dirty) event.preventDefault(); };
    window.addEventListener('beforeunload', unload); window.addEventListener(BEFORE_REFRESH, update);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener(BEFORE_REFRESH, update); };
  }, [dirty, storageWarning, needsCheck]);
  const save = async event => {
    event?.preventDefault(); const scope = live.current;
    if (locked.current || needsCheck || !owner || scope?.key !== key) return null;
    const validation = options.current.validate?.(current.current);
    if (validation) { setError(validation); return null; }
    locked.current = true; setBusy(true); setError(''); setNotice('');
    const old = currentRecord.current;
    const submitted = copy(current.current);
    const body = options.current.payload(submitted, old);
    pending.current = { creating: !old, expected: options.current.values({ ...old, ...body }), submitted }; persist();
    try {
      const response = old ? await axios.put(API + readPath, body, { timeout: 20000 }) : await axios.post(API + createPath, body, { timeout: 20000 });
      if (live.current !== scope) return null;
      let data = response.data;
      if (!data?.id) data = await read();
      if (live.current !== scope) return null;
      if (!data?.id || old && data.id !== old.id || !matchesPending(options.current.values(data))) throw new Error('unverified_profile');
      pending.current = null; setNeedsCheck(false); install(data, options.current.values(data));
      const waiting = data.approved && data.review_state?.pending?.kind === 'critical';
      setNotice(waiting ? 'Kaydedildi. Değişikliğin ekibimizin incelemesinde; onaylanana kadar mevcut bilgilerin görünür ve onayın devam ediyor.'
        : data.approved ? 'Profilin kaydedildi; mevcut onayın devam ediyor.' : 'Profilin kaydedildi. Ekip onayı bekleniyor.');
      return data;
    } catch (e) {
      if (live.current !== scope) return null;
      const status = e.response?.status, uncertain = !status || status >= 500;
      if (!uncertain) pending.current = null;
      setNeedsCheck(uncertain || status === 409); persist();
      setError(uncertain ? 'Kaydın sonucu doğrulanamadı. Yazdıkların korundu; tekrar göndermeden önce “Kaydı kontrol et” düğmesini kullan.'
        : status === 409 ? 'Profil başka bir yerde değişti. Yazdıkların korundu; önce güncel kaydı kontrol et.' : profileError(e));
      return null;
    } finally { if (live.current === scope) { locked.current = false; setBusy(false); } }
  };
  const adoptExternal = data => {
    if (!data?.id || currentRecord.current?.id !== data.id || live.current?.key !== key) return;
    install(data, mergeProfileDraft(baseline.current, current.current, options.current.values(data)));
  };
  const discard = () => {
    if (locked.current || needsCheck) return;
    current.current = copy(baseline.current); setFormState(current.current); persist(); setError(''); setNotice('Kaydedilmiş profil bilgilerine döndün.');
  };
  return { record, form, setForm, loading, busy, loadError, error, notice, dirty, needsCheck, storageWarning, save, reload, discard, adoptExternal };
}
export default function ProfileEditorStatus({ editor, disabled = false }) {
  return <section className="profile-editor-status" aria-label="Profil kayıt durumu">
    {editor.busy && <p role="status">İşlem sürüyor; profil alanları tamamlanana kadar kilitli.</p>}
    {editor.notice && <p role="status">{editor.notice}</p>}
    {editor.error && <p role="alert">{editor.error}</p>}
    {editor.needsCheck && <button type="button" className="action-secondary" disabled={editor.busy || disabled} onClick={() => editor.reload(true)}>Kaydı kontrol et</button>}
    {editor.dirty && !editor.busy && <p data-testid="profile-unsaved">Kaydedilmemiş değişikliklerin var. Taslağın bu sekmede korunur; hesaptan çıkışta temizlenir.</p>}
    {editor.storageWarning && <p role="alert">Tarayıcı taslağı kalıcı saklayamıyor. Bilgilerini kaydetmeden bu sayfayı kapatma veya yenileme.</p>}
    {editor.dirty && !editor.needsCheck && <button type="button" className="text-action" disabled={editor.busy || disabled} onClick={editor.discard}>Kaydedilmiş bilgilere dön</button>}
    {!editor.dirty && !editor.busy && !editor.needsCheck && !editor.notice && <p>Yeni bir değişiklik yaptığında kaydetme düğmesi açılır.</p>}
  </section>;
}
